import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Field, FilePicker, InfoTip, inputCls } from '../components/shared/FormKit';
import { FormValidationProvider, useFormValidation, FieldInvalidInline } from '../components/shared/formValidation';
import SearchSelect from '../components/shared/SearchSelect';
import { FieldCommentInline } from '../components/workflow/fieldComments';
import { FormShell, FormSection } from '../components/workflow/FormShell';
import {
  emptyEngagementFields, PieFields, UltimateParentField, SsicActivityFields,
  companiesOf, profileSplitGaps, profileSplitJson,
} from '../components/shared/EngagementFormFields';
import {
  emptyScreeningAnswers, screeningRequiredGaps, appendScreeningAnswers, ScreeningQuestionFields,
} from '../components/shared/ScreeningFields';
import { CompanyChecklistToolbar } from '../components/shared/EngagementCompanies';
import { fetchJSON } from '../utils/api';
import { hintOf } from '../utils/uploadKinds';

// Screening — the standalone submission form (docs/modules/workflow-screening.md).
// A background check QM performs on a client that needs one outside a
// registration: the client group and its companies, the client identity
// (PIE, Ultimate Parent, SSIC codes, principal activities — owner
// 2026-09-11: only these from the Registration profile), the six screening
// questions and optional supporting files.
// No FYE, no Date of Appointment, no people, no BizFile — nothing here creates
// an engagement. QM completes the request by attaching the screening PDF.

export const emptyScreeningForm = () => ({
  ...emptyEngagementFields(),
  ...emptyScreeningAnswers(),
  groupId: '', entityIds: [],
});

/** Client-side required check → `[{ key, label }]` (server re-validates). */
export const screeningRequestGaps = (f) => {
  const gaps = [];
  if (!f.groupId) gaps.push({ key: 'clientGroup', label: 'Client Group' });
  else if (f.entityIds.length === 0) gaps.push({ key: 'entityNames', label: 'Company (At Least One)' });
  // The shared answer, unless the companies answer it on their own (then profileSplitGaps).
  const split = f.profileSplit || {};
  const own = (k) => (f.entityIds || []).length > 1 && Object.keys(split[k] || {}).length > 0;
  if (!own('isPie')) {
    if (f.isPie === '') gaps.push({ key: 'isPie', label: 'Public Interest Entity (PIE)?' });
    else if (f.isPie === 'yes' && !f.pieType) gaps.push({ key: 'pieType', label: 'Type of PIE' });
    else if (f.isPie === 'yes' && f.pieType === 'Others' && !String(f.pieTypeOther || '').trim()) gaps.push({ key: 'pieType', label: 'Type of PIE (if Others)' });
  }
  if (!String(f.ultimateParent || '').trim()) gaps.push({ key: 'ultimateParent', label: 'Ultimate Parent' });
  if (!own('ssicPrimary') && !String(f.ssicPrimary || '').trim()) gaps.push({ key: 'ssicPrimary', label: 'SSIC Code Primary' });
  if (!own('principalActivity1') && !String(f.principalActivity1 || '').trim()) gaps.push({ key: 'principalActivity1', label: 'Principal Activity 1' });
  gaps.push(...profileSplitGaps(f, { fields: ['pie', 'ssicPrimary', 'ssicSecondary', 'principalActivity1', 'principalActivity2'] }));
  gaps.push(...screeningRequiredGaps(f));
  return gaps;
};

/** FormData for POST / PATCH — one shape for both. */
export const screeningRequestFormData = (f, files) => {
  const form = new FormData();
  form.set('entityIds', JSON.stringify(f.entityIds));
  form.set('isPie', f.isPie === 'yes' ? 'true' : f.isPie === 'no' ? 'false' : '');
  form.set('pieType', f.isPie === 'yes' ? (f.pieType || '') : '');
  form.set('pieTypeOther', f.isPie === 'yes' ? (f.pieTypeOther || '') : '');
  form.set('profileSplit', profileSplitJson(f));
  form.set('ultimateParent', f.ultimateParent || '');
  form.set('ssicPrimary', f.ssicPrimary || '');
  form.set('ssicSecondary', f.ssicSecondary || '');
  form.set('principalActivity1', f.principalActivity1 || '');
  form.set('principalActivity2', f.principalActivity2 || '');
  appendScreeningAnswers(form, f);
  form.set('remarks', f.remarks || '');
  (files || []).forEach((x) => form.append('files', x));
  return form;
};

/**
 * Companies of one client group — the same picker shape as Registration's.
 * Shared with the Management screening record form, which passes
 * `caption`, `onGroupChange` (extra state to reset) and `rowExtra(entity,
 * ticked)` for the per-company engagement link.
 */
export const ScreeningCompaniesPicker = ({
  f, set, options,
  caption = "Companies (Client's Name + UEN, from Clients & Entities):",
  onGroupChange = null, rowExtra = null,
}) => {
  const { groups = [], entities = [] } = options || {};
  const groupEntities = useMemo(() => {
    if (!f.groupId) return [];
    return entities.filter((e) => String(e.clientGroupId) === String(f.groupId));
  }, [entities, f.groupId]);
  const toggle = (id) => set('entityIds', f.entityIds.includes(id)
    ? f.entityIds.filter((x) => x !== id)
    : [...f.entityIds, id]);
  return (
    <>
      <Field label="Client Group" required field="clientGroup">
        <SearchSelect
          value={f.groupId}
          onChange={(v) => { set('groupId', v); set('entityIds', []); if (onGroupChange) onGroupChange(); }}
          options={groups.map((g) => ({ value: g.id, label: g.name }))}
          placeholder="Search group…"
        />
      </Field>
      {f.groupId && (
        groupEntities.length === 0 ? (
          <p className="text-sm text-amber-700">This group has no registered companies yet — register them through New Client first.</p>
        ) : (
          <div className="border border-slate-200 rounded-lg p-3 bg-slate-50">
            <CompanyChecklistToolbar
              caption={caption}
              total={groupEntities.length}
              selected={groupEntities.filter((e) => f.entityIds.includes(e.id)).length}
              onSelectAll={() => set('entityIds', groupEntities.map((e) => e.id))}
              onClearAll={() => set('entityIds', [])}
            />
            <ul className="space-y-1">
              {groupEntities.map((e) => (
                <li key={e.id} className="flex flex-wrap items-center gap-2 text-sm text-slate-700">
                  <input type="checkbox" checked={f.entityIds.includes(e.id)} onChange={() => toggle(e.id)} />
                  <span>{e.entityName}</span>
                  <span className="text-xs text-slate-500 font-mono">{e.uen || '—'}</span>
                  {rowExtra && rowExtra(e, f.entityIds.includes(e.id))}
                </li>
              ))}
            </ul>
          </div>
        )
      )}
      <FieldInvalidInline field="entityNames" text="Required — tick at least one company" />
      <FieldCommentInline field="entityNames" />
    </>
  );
};

// The shared form body: ① companies → ② client profile → ③ screening questions.
// Attachments / remarks stay in the page (New vs Modify differ).
export const ScreeningRequestFormFields = ({ f, set, options }) => (
  <>
    <FormSection step={1} title="Companies" hint={<>Pick the client group, then tick every company this screening covers. <InfoTip text="Register missing companies through New Client first." /></>}>
      <ScreeningCompaniesPicker f={f} set={set} options={options} />
    </FormSection>
    <FormSection step={2} title="Client Profile">
      <PieFields f={f} set={set} anchor={(k) => k} companies={companiesOf(f, options)} />
      <UltimateParentField f={f} set={set} anchor={(k) => k} />
      <SsicActivityFields f={f} set={set} anchor={(k) => k} companies={companiesOf(f, options)} />
    </FormSection>
    <FormSection step={3} title="Screening" hint="What QM needs to know to run the background check.">
      <ScreeningQuestionFields f={f} set={set} anchor={(k) => k} companies={companiesOf(f, options)} />
    </FormSection>
  </>
);

const WorkflowScreeningNew = () => {
  const navigate = useNavigate();
  const [options, setOptions] = useState(null);
  const [f, setF] = useState(emptyScreeningForm());
  const [files, setFiles] = useState([]);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));

  useEffect(() => {
    (async () => {
      try { setOptions(await fetchJSON('/api/workflow/screening/options')); }
      catch (e) { setError(e.message); }
    })();
  }, []);

  const gaps = useMemo(() => screeningRequestGaps(f), [f]);
  const validation = useFormValidation(gaps);

  const submit = async () => {
    if (!validation.validate()) { setError(null); return; }
    setSaving(true); setError(null);
    try {
      const data = await fetchJSON('/api/workflow/screening', { method: 'POST', body: screeningRequestFormData(f, files) });
      navigate(`/workflow/screening/${data.request.id}`);
    } catch (e) { setError(e.message); setSaving(false); }
  };

  return (
    <FormShell
      backTo="/workflow/screening"
      backLabel="Screening"
      title="New Screening Request"
      subtitle="Request a QM background check."
      error={error || validation.error}
      onSubmit={submit}
      submitting={saving}
    >
      <FormValidationProvider value={validation.providerValue}>
        <ScreeningRequestFormFields f={f} set={set} options={options} />

        <FormSection step={4} title="Attachments">
          <Field label="Supporting Files" hint={hintOf('docsSheetsImages', { multiple: true })}>
            <FilePicker multiple kind="docsSheetsImages"
              onChange={(e) => setFiles(Array.from(e.target.files || []))}
              files={files} />
          </Field>
        </FormSection>

        <FormSection step={5} title="Remarks">
          <Field label="Remarks">
            <textarea rows="2" value={f.remarks} onChange={(e) => set('remarks', e.target.value)} className={inputCls} />
          </Field>
        </FormSection>
      </FormValidationProvider>
    </FormShell>
  );
};

export default WorkflowScreeningNew;
